import { addKsql, DynamicDataTypeEnum, KuxCategoryMetas, ProviderSettings } from '@kaltura-ott/tvpil';
import { computed } from 'mobx';
import { container, singleton } from 'tsyringe';

import type { AppParametersStore } from '../app/app-params-store';
import type { DmsStore } from '../dms/dms-store';
import type { Series } from '../entities/Series';
import type { Subscription } from '../entities/Subscription';
import type { VodAsset } from '../entities/VodAsset';
import type { UserStore } from '../user/user-store';

@singleton()
export class ProvidersRepository {
  purchasedProviders: string[] = [];
  availableProviders: string[] = [];
  unPurchasedProviders: string[] = [];
  providerSettings: ProviderSettings = {};

  init() {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    const userStore = container.resolve<UserStore>('userStore');

    this.providerSettings = dmsStore.dms.providers;

    Object.keys(this.providerSettings).forEach((provider) => {
      const { activationUrl, displayName, userDynamicDataCodeKey } = this.providerSettings[provider];

      this.providerSettings[provider].isConfigured = true;
      this.providerSettings[provider].title = displayName;

      const requiresUserCode = Boolean(userDynamicDataCodeKey);

      if (requiresUserCode) {
        const userCode = userStore?.getFromDynamicData(userDynamicDataCodeKey as DynamicDataTypeEnum);

        this.providerSettings[provider].code = userCode;
        this.providerSettings[provider].isConfigured = Boolean(userCode) && Boolean(activationUrl);
      }

      this.providerSettings[provider].fullActivationUrl = [
        this.providerSettings[provider].activationUrl,
        this.providerSettings[provider].code,
      ]
        .filter(Boolean)
        .join('');
    });
  }

  calculateAssetProviders(assetMetas: KuxCategoryMetas): string | undefined {
    return Object.keys(this.providerSettings).find((provider) => {
      const providerData = this.providerSettings[provider];

      if (!providerData || !providerData.metaKey) {
        return;
      }

      const assetProvider = (assetMetas[providerData.metaKey] as unknown as string)?.toLowerCase();

      return assetProvider === providerData.metaValue?.toLowerCase();
    });
  }

  providerGridLogo(gridName?: string): string | undefined {
    if (!gridName) {
      return;
    }

    const provider = Object.keys(this.providerSettings).find((provider) =>
      gridName.toLowerCase().includes(provider.toLowerCase()),
    );

    if (provider && this.providerSettings[provider].enabled) {
      return this.providerSettings[provider].providerImageUrl;
    }
  }

  providerAssetLogo(providerName?: string): string | undefined {
    if (providerName && this.providerSettings[providerName] && this.providerSettings[providerName].enabled) {
      return this.providerSettings[providerName].providerImageUrl;
    }
  }

  @computed get searchKsql(): string {
    let ksql = '';

    // exclude unpurchased and disabled providers
    this.unPurchasedProviders.map((provider) => {
      const additionalKsql = this.providerSettings[provider]
        ? `(and ${this.providerSettings[provider].metaKey} != '${this.providerSettings[provider].metaValue}')`
        : '';

      ksql = addKsql(ksql, additionalKsql);
    });

    return ksql;
  }

  calculateAvailableProviders() {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    const userStore = container.resolve<UserStore>('userStore');
    const isUserPrePaid =
      userStore.getFromDynamicData(DynamicDataTypeEnum.customerType) === String(dmsStore.dms.prePaidUserId);

    Object.keys(this.providerSettings).forEach((provider) => {
      const providerData = this.providerSettings[provider];

      if (!isUserPrePaid && providerData.enabled) {
        this.availableProviders.push(provider);
      }
    });
  }

  calculatePurchasedProviders(subscriptions: Subscription[]) {
    Object.keys(this.providerSettings).forEach((provider) => {
      const purchasedSubscription = subscriptions.find(
        (subscription) => subscription.assetPackage.data.tags['ExternalPackageProviderName']?.includes(provider),
      );

      if (purchasedSubscription && this.providerSettings[provider].enabled) {
        this.purchasedProviders.push(provider);
      } else {
        this.unPurchasedProviders.push(provider);
      }
    });
  }

  isDeviceAllowedDeepLink(providerName?: string): boolean {
    const appParams = container.resolve<AppParametersStore>('appParamsStore');

    const provider = providerName && this.providerSettings[providerName];
    if (provider && provider.devicesNotAllowedDeepLinkBrandIds) {
      return provider.devicesNotAllowedDeepLinkBrandIds !== appParams.currentDevice.brandId.toString();
    }
    return false;
  }

  isDeviceAllowedGoDirectlyToStore(providerName?: string): boolean {
    const appParams = container.resolve<AppParametersStore>('appParamsStore');

    const provider = providerName && this.providerSettings[providerName];
    if (provider && provider.devicesNotAllowedGoDirectlyToStoreBrandIds) {
      return provider.devicesNotAllowedGoDirectlyToStoreBrandIds !== appParams.currentDevice.brandId.toString();
    }
    return false;
  }

  isAssetPlayOnExternalApp(asset: Series | VodAsset): boolean {
    // @ts-expect-error TODO Fix
    return !!asset.data.metas?.Deeplink && !!asset.data.metas?.DeeplinkURN && asset?.data?.mediaFiles.length <= 0;
  }

  resetRepository() {
    this.availableProviders = [];
    this.purchasedProviders = [];
    this.unPurchasedProviders = [];
  }
}
